/*
 * assets/search.js
 * Predictive search for the ucon acrobatics theme.
 *
 * Defines <predictive-search>: a header search trigger that opens a full-screen
 * overlay (sliding in from the right) with a debounced, keyboard-navigable
 * predictive layout backed by Shopify's Ajax Predictive Search API
 * (/search/suggest.json).
 *
 * Layout: a centered field in the upper half, then two columns - Products (3/4)
 * and Collections (1/4). Before a query is typed the columns show the
 * server-rendered defaults (bestsellers + collection links). As the shopper
 * types, predictive results replace the defaults in place. A link below leads to
 * the full search results page.
 *
 * Implements the WAI-ARIA combobox (listbox popup) pattern: arrow keys move a
 * roving aria-activedescendant across rendered options, Enter follows the active
 * option (or submits the form), and Escape clears / closes. Vanilla JS, no deps.
 */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('predictive-search')) {
    return;
  }

  var DEBOUNCE_MS = 220;
  var MIN_CHARS = 2;
  // One row in the products column: 3 up to 1440px, 4 above.
  var PRODUCT_LIMIT = 3;
  var PRODUCT_LIMIT_XL = 4;
  var COLLECTION_LIMIT = 8;
  var CACHE_LIMIT = 50;
  var REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Highlight the matched terms inside a label without trusting raw markup.
  function highlight(text, terms) {
    var safe = escapeHtml(text);
    var query = (terms || '').trim();
    if (!query) return safe;
    var tokens = query.split(/\s+/).filter(Boolean).map(function (t) {
      return t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    });
    if (!tokens.length) return safe;
    var re = new RegExp('(' + tokens.join('|') + ')', 'gi');
    return safe.replace(re, '<mark>$1</mark>');
  }

  // Predictive prices are decimal strings in major units (e.g. "49.99"), so the
  // formatter works on a Number of dollars/euros, not cents. Mirrors Shopify's
  // money_format placeholder set so the merchant's configured format is honored.
  function formatMoney(amount, format) {
    var value = Number(amount);
    if (!isFinite(value)) return '';
    var fmt = format || '${{amount}}';

    function withCommas(number) {
      var parts = number.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }

    function format2(decimals, thousands, decimalMark) {
      var fixed = value.toFixed(decimals);
      var withSep = withCommas(fixed);
      if (thousands !== ',' || decimalMark !== '.') {
        withSep = withSep
          .replace(/,/g, '\x00')
          .replace(/\./g, decimalMark)
          .replace(/\x00/g, thousands);
      }
      return withSep;
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, token) {
      switch (token) {
        case 'amount':
          return format2(2, ',', '.');
        case 'amount_no_decimals':
          return format2(0, ',', '.');
        case 'amount_with_comma_separator':
          return format2(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return format2(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return format2(2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return format2(0, ' ', ',');
        case 'amount_with_space_separator':
          return format2(2, ' ', ',');
        default:
          return format2(2, ',', '.');
      }
    });
  }

  var PredictiveSearch = function () {
    return Reflect.construct(HTMLElement, [], PredictiveSearch);
  };
  PredictiveSearch.prototype = Object.create(HTMLElement.prototype);
  PredictiveSearch.prototype.constructor = PredictiveSearch;

  PredictiveSearch.prototype.connectedCallback = function () {
    this.searchUrl = this.getAttribute('data-search-url') || '/search';
    this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
    this.labels = {
      products: this.getAttribute('data-label-products') || 'Products',
      bestsellers: this.getAttribute('data-label-bestsellers') || 'Bestsellers',
      empty: this.getAttribute('data-label-empty') || 'No matches yet. Keep typing.',
      viewall: this.getAttribute('data-label-viewall') || 'Search all results for "%%TERMS%%"',
      countOne: this.getAttribute('data-label-count-one') || '%%COUNT%% suggestion available',
      countOther: this.getAttribute('data-label-count-other') || '%%COUNT%% suggestions available'
    };

    this.trigger = this.querySelector('[data-hsearch-open]');
    this.overlay = this.querySelector('[data-hsearch-overlay]');
    this.form = this.querySelector('[data-hsearch-form]');
    this.input = this.querySelector('[data-hsearch-input]');
    this.clearBtn = this.querySelector('[data-hsearch-clear]');
    this.resultsWrap = this.querySelector('[data-hsearch-results-wrap]');
    this.productsEl = this.querySelector('[data-hsearch-products]');
    this.productsEyebrow = this.querySelector('[data-hsearch-products-eyebrow]');
    this.collectionsEl = this.querySelector('[data-hsearch-collections]');
    this.collectionsCol = this.querySelector('[data-hsearch-collections-col]');
    this.viewall = this.querySelector('[data-hsearch-viewall]');
    this.viewallLabel = this.querySelector('[data-hsearch-viewall-label]');
    this.status = this.querySelector('[data-hsearch-status]');
    this.closeEls = this.querySelectorAll('[data-hsearch-close]');

    if (!this.trigger || !this.overlay || !this.input || !this.productsEl) return;

    // Server-rendered defaults (bestsellers + collection links), restored
    // whenever the field is cleared or the overlay reopens.
    this.defaultProducts = this.productsEl.innerHTML;
    this.defaultCollections = this.collectionsEl ? this.collectionsEl.innerHTML : '';
    this.defaultViewallHref = this.viewall ? this.viewall.getAttribute('href') : null;
    this.defaultViewallLabel = this.viewallLabel ? this.viewallLabel.textContent : '';

    this.isOpen = false;
    this.activeIndex = -1;
    this.options = [];
    this.controller = null;
    // Insertion-ordered LRU of suggest.json results, capped at CACHE_LIMIT so a
    // long session can't grow it (or serve stale prices) without bound.
    this.cache = new Map();
    this.debounceTimer = null;

    this.onTriggerClick = this.open.bind(this);
    this.onInput = this.handleInput.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onClear = this.handleClear.bind(this);
    this.onDocKeydown = this.handleDocKeydown.bind(this);
    this.onSubmit = this.handleSubmit.bind(this);

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    if (this.form) this.form.addEventListener('submit', this.onSubmit);
    if (this.clearBtn) this.clearBtn.addEventListener('click', this.onClear);
    var self = this;
    Array.prototype.forEach.call(this.closeEls, function (el) {
      el.addEventListener('click', function () {
        self.close();
      });
    });
  };

  PredictiveSearch.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;
    // Open clean: empty field, defaults showing.
    this.input.value = '';
    if (this.clearBtn) this.clearBtn.hidden = true;
    this.showDefaults();
    this.overlay.hidden = false;
    // Force a reflow so the enter transition runs from the hidden state.
    void this.overlay.offsetWidth;
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.lastFocused = document.activeElement;
    document.addEventListener('keydown', this.onDocKeydown, true);
    // Lock background scroll while the overlay is open. The overlay is not a
    // native <dialog>, so the critical.css :has(dialog[scroll-lock][open]) rule
    // doesn't cover it - lock html + body overflow directly instead.
    this.lockScroll();
    var self = this;
    window.requestAnimationFrame(function () {
      self.input.focus();
    });
  };

  PredictiveSearch.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.onDocKeydown, true);
    this.unlockScroll();
    // Drop any in-flight predictive request so a late response can't render
    // into the now-hidden overlay.
    window.clearTimeout(this.debounceTimer);
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
    this.setLoading(false);
    this.collapse();
    var self = this;
    var hide = function () {
      self.overlay.hidden = true;
    };
    if (REDUCE_MOTION) {
      hide();
    } else {
      window.setTimeout(hide, 300);
    }
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    } else {
      this.trigger.focus();
    }
  };

  // Mirror the native <dialog scroll-lock> behaviour (critical.css) for this
  // non-dialog overlay: freeze html + body overflow on open, restore on close.
  PredictiveSearch.prototype.lockScroll = function () {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  };

  PredictiveSearch.prototype.unlockScroll = function () {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  };

  // Trap focus inside the dialog and route global keys while open.
  PredictiveSearch.prototype.handleDocKeydown = function (e) {
    if (!this.isOpen) return;
    if (e.key === 'Escape') {
      // Esc clears a non-empty field first, then closes on a second press.
      if (this.input.value) {
        e.preventDefault();
        this.handleClear();
        return;
      }
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key === 'Tab') {
      this.trapFocus(e);
    }
  };

  PredictiveSearch.prototype.trapFocus = function (e) {
    var focusables = this.overlay.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    var visible = Array.prototype.filter.call(focusables, function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!visible.length) return;
    var first = visible[0];
    var last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  PredictiveSearch.prototype.handleInput = function () {
    var value = this.input.value;
    if (this.clearBtn) this.clearBtn.hidden = value.length === 0;

    window.clearTimeout(this.debounceTimer);
    var query = value.trim();
    if (query.length < MIN_CHARS) {
      this.showDefaults();
      return;
    }
    var self = this;
    this.debounceTimer = window.setTimeout(function () {
      self.search(query);
    }, DEBOUNCE_MS);
  };

  PredictiveSearch.prototype.handleClear = function () {
    this.input.value = '';
    if (this.clearBtn) this.clearBtn.hidden = true;
    this.showDefaults();
    this.input.focus();
  };

  PredictiveSearch.prototype.handleSubmit = function (e) {
    // If an option is active, follow it instead of submitting the raw query.
    if (this.activeIndex > -1 && this.options[this.activeIndex]) {
      e.preventDefault();
      window.location.href = this.options[this.activeIndex].href;
      return;
    }
    if (!this.input.value.trim()) {
      e.preventDefault();
    }
  };

  // Bounded LRU: re-inserting moves the key to the most-recently-used end, and
  // the oldest entry is evicted once the cap is exceeded.
  PredictiveSearch.prototype.cacheSet = function (query, resources) {
    if (this.cache.has(query)) this.cache.delete(query);
    this.cache.set(query, resources);
    while (this.cache.size > CACHE_LIMIT) {
      this.cache.delete(this.cache.keys().next().value);
    }
  };

  PredictiveSearch.prototype.search = function (query) {
    if (this.cache.has(query)) {
      var cached = this.cache.get(query);
      // Refresh recency on a hit.
      this.cache.delete(query);
      this.cache.set(query, cached);
      this.render(cached.resources, query, cached.cardsHtml);
      return;
    }
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    var signal = this.controller.signal;

    // suggest.json drives the collections column (and is the product fallback);
    // request just the two shown types.
    var params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product,collection');
    params.set('resources[limit]', '8');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');

    var url = '/search/suggest.json?' + params.toString();

    // Product cards are rendered server-side via the Section Rendering API -
    // suggest.json exposes no options or metafields, and the cards show series,
    // available sizes, and colour swatches. options[prefix]=last keeps the
    // type-ahead matching of suggest.json.
    var cardsParams = new URLSearchParams();
    cardsParams.set('q', query);
    cardsParams.set('type', 'product');
    cardsParams.set('options[prefix]', 'last');
    cardsParams.set('section_id', 'predictive-search-cards');
    var cardsUrl = this.searchUrl + '?' + cardsParams.toString();

    this.setLoading(true);

    var self = this;
    var suggestPromise = fetch(url, { signal: signal, headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Predictive search request failed');
        return r.json();
      })
      .then(function (data) {
        return (data && data.resources && data.resources.results) || {};
      });

    // A null cardsHtml means "fall back to suggest.json cards" - the SRA fetch
    // failing must never take the whole predictive layer down with it.
    var cardsPromise = fetch(cardsUrl, { signal: signal })
      .then(function (r) {
        if (!r.ok) throw new Error('Card render request failed');
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var wrap = doc.querySelector('[data-hsearch-sra-cards]');
        return wrap ? wrap.innerHTML : null;
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') throw err;
        return null;
      });

    Promise.all([suggestPromise, cardsPromise])
      .then(function (results) {
        var resources = results[0];
        var cardsHtml = results[1];
        self.cacheSet(query, { resources: resources, cardsHtml: cardsHtml });
        // The overlay may have been closed while this request was in flight;
        // don't render into a hidden overlay.
        if (!self.isOpen) return;
        self.render(resources, query, cardsHtml);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        self.showDefaults();
      })
      .finally(function () {
        self.setLoading(false);
      });
  };

  PredictiveSearch.prototype.setLoading = function (state) {
    this.setAttribute('data-loading', state ? 'true' : 'false');
  };

  // Restore the server-rendered defaults (bestsellers + collection links) and the
  // default "browse all" link. Used on open, on clear, and on request failure.
  PredictiveSearch.prototype.showDefaults = function () {
    this.productsEl.innerHTML = this.defaultProducts;
    if (this.productsEyebrow) this.productsEyebrow.textContent = this.labels.bestsellers;
    if (this.collectionsEl) this.collectionsEl.innerHTML = this.defaultCollections;
    if (this.collectionsCol) {
      this.collectionsCol.hidden = this.defaultCollections.trim() === '';
    }
    this.resetViewall();
    this.setStatus('');
    this.collectOptions();
    this.activeIndex = -1;
    this.input.setAttribute('aria-activedescendant', '');
    this.input.setAttribute('aria-expanded', this.options.length ? 'true' : 'false');
  };

  PredictiveSearch.prototype.render = function (resources, query, cardsHtml) {
    var perRow = window.matchMedia('(min-width: 1440px)').matches
      ? PRODUCT_LIMIT_XL
      : PRODUCT_LIMIT;
    var collections = (resources.collections || []).slice(0, COLLECTION_LIMIT);

    // Products column (3/4): prefer the server-rendered cards (series + sizes +
    // swatches, Section Rendering API); fall back to cards built from the
    // suggest.json payload when that fetch failed. Empty shows a short note.
    var productCount = 0;
    if (this.productsEyebrow) this.productsEyebrow.textContent = this.labels.products;
    if (cardsHtml != null) {
      productCount = this.renderServerCards(cardsHtml, query, perRow);
    }
    if (cardsHtml == null) {
      var products = (resources.products || []).slice(0, perRow);
      productCount = products.length;
      if (products.length) {
        this.productsEl.innerHTML = this.renderProductCards(products, query);
      }
    }
    if (!productCount) {
      this.productsEl.innerHTML =
        '<p class="hsearch__empty">' + escapeHtml(this.labels.empty) + '</p>';
    }

    // Collections column (1/4) - hidden when this query has no collections.
    if (this.collectionsEl) {
      if (collections.length) {
        this.collectionsEl.innerHTML = this.renderCollections(collections, query);
        if (this.collectionsCol) this.collectionsCol.hidden = false;
      } else {
        this.collectionsEl.innerHTML = '';
        if (this.collectionsCol) this.collectionsCol.hidden = true;
      }
    }

    this.setViewall(query);
    this.collectOptions();
    this.activeIndex = -1;
    this.input.setAttribute('aria-activedescendant', '');

    var total = productCount + collections.length;
    if (total) {
      this.expand();
      this.announce(this.options.length);
    } else {
      this.input.setAttribute('aria-expanded', 'false');
      this.setStatus('');
    }
  };

  // Swap in the Liquid-rendered cards (Section Rendering API), trimmed to one
  // row, with the matched terms highlighted in the name. Returns how many cards
  // were shown. The HTML comes from our own theme section, not user input.
  PredictiveSearch.prototype.renderServerCards = function (cardsHtml, query, limit) {
    var doc = new DOMParser().parseFromString(
      '<div>' + cardsHtml + '</div>',
      'text/html'
    );
    var cards = Array.prototype.slice.call(
      doc.querySelectorAll('.hsearch__card')
    ).slice(0, limit);

    this.productsEl.innerHTML = '';
    var self = this;
    cards.forEach(function (card) {
      var name = card.querySelector('.hsearch__card-name');
      if (name) name.innerHTML = highlight(name.textContent, query);
      self.productsEl.appendChild(card);
    });
    return cards.length;
  };

  PredictiveSearch.prototype.renderProductCards = function (products, query) {
    var self = this;
    return products
      .map(function (product) {
        // Predictive products expose `image` (string URL); a matched variant may
        // also carry `featured_image.url`.
        var imgUrl =
          (product.featured_image && product.featured_image.url) ||
          product.image ||
          '';
        var media;
        if (imgUrl) {
          var src = self.sizedImage(imgUrl, 400);
          media =
            '<span class="hsearch__card-media"><img class="hsearch__card-img" src="' +
            escapeHtml(src) +
            '" alt="" width="200" height="200" loading="lazy"></span>';
        } else {
          media =
            '<span class="hsearch__card-media"><span class="hsearch__card-ph" aria-hidden="true">' +
            '<i class="ph ph-image"></i></span></span>';
        }

        // Prices arrive as decimal strings; price_min is the "from" price.
        // Markup mirrors snippets/price.liquid: price, struck compare-at, "-N%"
        // rounded to the nearest whole number. The compare-at should be the SAME variant's, so it is
        // read off the variant at price_min when the response carries variants;
        // the predictive endpoint currently sends an empty list, so it falls
        // back to compare_at_price_min, which Shopify takes over on-sale
        // variants only (0.00 when none is on sale).
        var priceValue = product.price_min != null ? product.price_min : product.price;
        var price = '';
        if (priceValue != null) {
          var cmp = product.compare_at_price_min;
          (product.variants || []).some(function (v) {
            if (Number(v.price) !== Number(priceValue)) return false;
            cmp = v.compare_at_price;
            return true;
          });
          price = '<span class="price hsearch__card-price"><span class="price__now">' +
            escapeHtml(self.priceFor(priceValue)) + '</span>';
          var nowCents = Math.round(Number(priceValue) * 100);
          var cmpCents = cmp != null ? Math.round(Number(cmp) * 100) : 0;
          if (cmpCents > nowCents) {
            price += ' <s class="price__was">' + escapeHtml(self.priceFor(cmp)) + '</s>';
            var pct = Math.round((cmpCents - nowCents) * 100 / cmpCents);
            if (pct > 0) price += ' <span class="price__badge">-' + pct + '%</span>';
          }
          price += '</span>';
        }

        return (
          '<a class="hsearch__card" role="option" aria-selected="false" href="' +
          escapeHtml(product.url) +
          '">' +
          media +
          '<span class="hsearch__card-name">' +
          highlight(product.title, query) +
          '</span>' +
          price +
          '</a>'
        );
      })
      .join('');
  };

  PredictiveSearch.prototype.renderCollections = function (collections, query) {
    return collections
      .filter(function (c) {
        return c.title && c.url;
      })
      .map(function (c) {
        return (
          '<a class="hsearch__collection" role="option" aria-selected="false" href="' +
          escapeHtml(c.url) +
          '">' +
          // Single span child: the link is flex (vertical centering), so wrapping
          // keeps the label's inner whitespace from collapsing between flex items.
          '<span class="hsearch__collection-label">' +
          highlight(c.title, query) +
          '</span>' +
          '</a>'
        );
      })
      .join('');
  };

  // Point the "view all" link at the full results page for the current query and
  // swap its label; resetViewall() restores the default (browse all) state.
  PredictiveSearch.prototype.setViewall = function (query) {
    if (!this.viewall) return;
    this.viewall.setAttribute(
      'href',
      this.searchUrl + '?q=' + encodeURIComponent(query) + '&type=product'
    );
    if (this.viewallLabel) {
      this.viewallLabel.textContent = this.labels.viewall.replace('%%TERMS%%', query);
    }
  };

  PredictiveSearch.prototype.resetViewall = function () {
    if (!this.viewall) return;
    if (this.defaultViewallHref) this.viewall.setAttribute('href', this.defaultViewallHref);
    if (this.viewallLabel) this.viewallLabel.textContent = this.defaultViewallLabel;
  };

  // Request a small thumbnail: swap a {width} placeholder if present, else add a
  // width query param the Shopify CDN understands. Falls back to the raw URL.
  PredictiveSearch.prototype.sizedImage = function (url, width) {
    if (!url) return url;
    if (url.indexOf('{width}') > -1) return url.replace('{width}', String(width));
    try {
      var u = new URL(url, window.location.origin);
      if (/cdn\.shopify\.com|\/cdn\/shop\//.test(u.href)) {
        u.searchParams.set('width', String(width));
        return u.toString();
      }
    } catch (e) {
      /* ignore and return raw url */
    }
    return url;
  };

  PredictiveSearch.prototype.priceFor = function (value) {
    // Predictive prices are decimal values in major units; format with the
    // shop's money_format so currency + separators match the storefront.
    return formatMoney(value, this.moneyFormat);
  };

  // Collect every option across both columns (DOM order) and give each a stable
  // id so aria-activedescendant can reference the roving highlight.
  PredictiveSearch.prototype.collectOptions = function () {
    var scope = this.resultsWrap || this.overlay;
    this.options = Array.prototype.slice.call(scope.querySelectorAll('[role="option"]'));
    this.options.forEach(function (el, i) {
      el.id = 'hsearch-opt-' + i;
    });
  };

  PredictiveSearch.prototype.expand = function () {
    this.input.setAttribute('aria-expanded', 'true');
  };

  PredictiveSearch.prototype.collapse = function () {
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-activedescendant', '');
    this.activeIndex = -1;
    this.options = [];
  };

  PredictiveSearch.prototype.handleKeydown = function (e) {
    var key = e.key;
    if (key === 'ArrowDown') {
      e.preventDefault();
      this.move(1);
    } else if (key === 'ArrowUp') {
      e.preventDefault();
      this.move(-1);
    } else if (key === 'Enter') {
      if (this.activeIndex > -1 && this.options[this.activeIndex]) {
        e.preventDefault();
        window.location.href = this.options[this.activeIndex].getAttribute('href');
      }
      // otherwise allow native form submit
    } else if (key === 'Home' && this.options.length) {
      e.preventDefault();
      this.setActive(0);
    } else if (key === 'End' && this.options.length) {
      e.preventDefault();
      this.setActive(this.options.length - 1);
    }
  };

  PredictiveSearch.prototype.move = function (delta) {
    if (!this.options.length) return;
    var next = this.activeIndex + delta;
    if (next < 0) next = this.options.length - 1;
    if (next >= this.options.length) next = 0;
    this.setActive(next);
  };

  PredictiveSearch.prototype.setActive = function (index) {
    if (this.activeIndex > -1 && this.options[this.activeIndex]) {
      var prev = this.options[this.activeIndex];
      prev.classList.remove('is-active');
      prev.setAttribute('aria-selected', 'false');
    }
    this.activeIndex = index;
    var el = this.options[index];
    if (!el) {
      this.input.setAttribute('aria-activedescendant', '');
      return;
    }
    el.classList.add('is-active');
    el.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', el.id);
    el.scrollIntoView({ block: 'nearest', behavior: REDUCE_MOTION ? 'auto' : 'smooth' });
  };

  PredictiveSearch.prototype.setStatus = function (text) {
    if (this.status) this.status.textContent = text;
  };

  PredictiveSearch.prototype.announce = function (count) {
    var tpl = count === 1 ? this.labels.countOne : this.labels.countOther;
    this.setStatus(tpl.replace('%%COUNT%%', String(count)));
  };

  window.customElements.define('predictive-search', PredictiveSearch);
})();
